import { and, or, type SQL, sql } from "drizzle-orm";
import z from "zod/v4";

// ──────────────────────────────────────────────────────────────────────────
// Field registry
// ──────────────────────────────────────────────────────────────────────────

export const COHORT_FIELD_DEFINITIONS = {
  gmvAmount: {
    kind: "numeric",
    label: "GMV Amount",
    tooltip: "Total GMV this creator has generated for your brand.",
  },
  unitSales: {
    kind: "numeric",
    label: "Unit Sales",
    tooltip: "Total units sold through this creator's content.",
  },
  distinctProductsSold: {
    kind: "numeric",
    label: "Unique Products Sold",
    tooltip: "Number of distinct products this creator has sold (paid orders via samples).",
  },
  distinctSkusSold: {
    kind: "numeric",
    label: "Unique SKUs Sold",
    tooltip: "Number of distinct SKUs this creator has sold (paid orders via samples).",
  },
} as const;

export type CohortFieldId = keyof typeof COHORT_FIELD_DEFINITIONS;

export type ComparisonOperator = "eq" | "gt" | "gte" | "lt" | "lte" | "between";

export type ConditionValue = number | [number, number];

// ──────────────────────────────────────────────────────────────────────────
// Zod schema — the canonical shape. Types below are derived (z.infer) rather
// than hand-declared, so the frontend and the API always agree on the exact
// shape the route validates.
// ──────────────────────────────────────────────────────────────────────────

const numericValue = z.union([z.number(), z.tuple([z.number(), z.number()])]);
const comparisonOperators = z.enum(["eq", "gt", "gte", "lt", "lte", "between"]);

const numericFieldSchema = (field: CohortFieldId) =>
  z.object({
    field: z.literal(field),
    operator: comparisonOperators,
    value: numericValue,
  });

const conditionSchema = z
  .discriminatedUnion("field", [
    numericFieldSchema("gmvAmount"),
    numericFieldSchema("unitSales"),
    numericFieldSchema("distinctProductsSold"),
    numericFieldSchema("distinctSkusSold"),
  ])
  .refine((c) => (c.operator === "between" ? Array.isArray(c.value) : !Array.isArray(c.value)), {
    message: "'between' requires a [min, max] tuple; other operators require a single number",
  });

const conditionGroupSchema = z.object({
  logic: z.enum(["all", "any"]),
  conditions: z.array(conditionSchema).min(1),
});

export const cohortRuleConditionsSchema = z.object({
  version: z.literal(1),
  when: z.array(conditionGroupSchema).min(1),
  except: z.array(conditionGroupSchema).max(1),
});

export type CohortCondition = z.infer<typeof conditionSchema>;
export type CohortConditionGroup = z.infer<typeof conditionGroupSchema>;
export type CohortRuleConditions = z.infer<typeof cohortRuleConditionsSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Condition summaries
// ──────────────────────────────────────────────────────────────────────────

export type CohortConditionRange = { min: number | null; max: number | null };

export type CohortConditionSummary = CohortConditionRange & {
  ruleId: string;
  field: CohortFieldId;
};

export function conditionRange(condition: CohortCondition): CohortConditionRange {
  switch (condition.operator) {
    case "eq":
      return { min: condition.value as number, max: condition.value as number };
    case "gt":
    case "gte":
      return { min: condition.value as number, max: null };
    case "lt":
    case "lte":
      return { min: null, max: condition.value as number };
    case "between": {
      const [min, max] = condition.value as [number, number];
      return { min, max };
    }
  }
}

/** The WHEN conditions of one rule, flattened in the order the builder shows them. */
export function summarizeRuleConditions(ruleId: string, tree: CohortRuleConditions): CohortConditionSummary[] {
  return tree.when
    .flatMap((group) => group.conditions)
    .map((condition) => ({ ruleId, field: condition.field, ...conditionRange(condition) }));
}

// ──────────────────────────────────────────────────────────────────────────
// Pure matcher
// ──────────────────────────────────────────────────────────────────────────

export interface CohortCreatorSignals {
  gmvAmount: number;
  unitSales: number;
  distinctProductsSold: number;
  distinctSkusSold: number;
}

function evalComparison(actual: number, operator: ComparisonOperator, value: ConditionValue): boolean {
  switch (operator) {
    case "eq":
      return actual === value;
    case "gt":
      return actual > (value as number);
    case "gte":
      return actual >= (value as number);
    case "lt":
      return actual < (value as number);
    case "lte":
      return actual <= (value as number);
    case "between": {
      const [lo, hi] = value as [number, number];
      return actual >= lo && actual <= hi;
    }
  }
}

function evalCondition(signals: CohortCreatorSignals, condition: CohortCondition): boolean {
  const raw = signals[condition.field];
  // `??` alone would only catch null/undefined, not NaN — and NaN compared with
  // any operator (including 'eq' against 0) is always false, so a NaN signal
  // would silently fail to match any rule rather than erroring.
  const actual = Number.isFinite(raw) ? raw : 0;
  return evalComparison(actual, condition.operator, condition.value);
}

function evalGroup(signals: CohortCreatorSignals, group: CohortConditionGroup): boolean {
  return group.logic === "all"
    ? group.conditions.every((c) => evalCondition(signals, c))
    : group.conditions.some((c) => evalCondition(signals, c));
}

/**
 * A creator matches iff all WHEN groups are true (groups are AND-ed) and, when
 * present, the EXCEPT group is NOT true (an exception match suppresses the rule).
 */
export function matchesConditions(signals: CohortCreatorSignals, tree: CohortRuleConditions): boolean {
  const whenMatch = tree.when.every((g) => evalGroup(signals, g));
  if (!whenMatch) return false;
  const exceptMatch = tree.except.length > 0 && tree.except.every((g) => evalGroup(signals, g));
  return !exceptMatch;
}

// ──────────────────────────────────────────────────────────────────────────
// SQL translator — mirrors matchesConditions/evalGroup/evalComparison above,
// but as a boolean SQL expression, so a condition tree can filter inside a
// query's WHERE instead of after loading every creator's signals into memory.
// ──────────────────────────────────────────────────────────────────────────

export type CohortFieldSQLMap = Record<CohortFieldId, SQL>;

function comparisonToSQL(column: SQL, operator: ComparisonOperator, value: ConditionValue): SQL {
  switch (operator) {
    case "eq":
      return sql`${column} = ${value}`;
    case "gt":
      return sql`${column} > ${value}`;
    case "gte":
      return sql`${column} >= ${value}`;
    case "lt":
      return sql`${column} < ${value}`;
    case "lte":
      return sql`${column} <= ${value}`;
    case "between": {
      const [lo, hi] = value as [number, number];
      return sql`${column} BETWEEN ${lo} AND ${hi}`;
    }
  }
}

function conditionToSQL(columns: CohortFieldSQLMap, condition: CohortCondition): SQL {
  return comparisonToSQL(columns[condition.field], condition.operator, condition.value);
}

function groupToSQL(columns: CohortFieldSQLMap, group: CohortConditionGroup): SQL {
  const parts = group.conditions.map((c) => conditionToSQL(columns, c));
  if (parts.length === 0) return group.logic === "all" ? sql`true` : sql`false`;
  return (group.logic === "all" ? and(...parts) : or(...parts)) as SQL;
}

/**
 * A creator matches iff all WHEN groups are true (groups are AND-ed) and, when
 * present, the EXCEPT group is NOT true — same semantics as `matchesConditions`,
 * expressed as SQL instead of evaluated against an in-memory signals object.
 */
export function conditionsToSQL(tree: CohortRuleConditions, columns: CohortFieldSQLMap): SQL {
  const whenSQL = tree.when.length > 0 ? (and(...tree.when.map((g) => groupToSQL(columns, g))) as SQL) : sql`true`;
  if (tree.except.length === 0) return whenSQL;
  const exceptSQL = and(...tree.except.map((g) => groupToSQL(columns, g))) as SQL;
  return and(whenSQL, sql`NOT (${exceptSQL})`) as SQL;
}
